import React, { useEffect, useState } from "react"
import { Backdrop, CircularProgress, Container, Grid, Snackbar, Tooltip } from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import backdropStyles from '../AgentSkillsManagement/styles.module.css'
import formStyles from '../TeamsManagement/styles.module.css'
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css'
import styles from './styles.module.css'
import { BlueBtn, GreyInverseBtn } from "../Styled/StyledButtons"
import { CarrierBlocksActions } from "../../actions/CarrierBlocksActions"
import {
    ICarrierCandidate, ICarrierRule, ICarrierRulesResponse, ICarrierStatsResponse,
} from "../../models/CarrierBlocks/CarrierRule"

/**
 * Carrier Blocker — the rule lists, what they did, and what they cost.
 *
 * Three deliberate choices, each answering a way this screen could mislead:
 *
 *   ENFORCED vs ACTIVE. A rule can be active and still do nothing, because it sits on the
 *   attack tier while the blocker runs in light mode. The server computes `enforced_now` and
 *   the row shows it, because "active" alone invites the conclusion that the blocker is broken
 *   when it is behaving exactly as configured.
 *
 *   TWO BOOKING NUMBERS. The replay audited all 23 `New Customer - Booked` tags the light list
 *   would have blocked: 20 were sub-minute with no lead, eleven of them 1–2 seconds, and
 *   exactly 3 were real. The raw count is off by ~8x, so it is shown greyed and labelled while
 *   the qualified count is the one presented as the cost.
 *
 *   CANDIDATES FROM DATA. Carriers move and a list seeded once decays, so the carriers not yet
 *   listed are ranked here by junk rate rather than remembered.
 */
const CarrierBlocks = () => {
    const [data, setData] = useState<ICarrierRulesResponse | null>(null)
    const [stats, setStats] = useState<ICarrierStatsResponse | null>(null)
    const [candidates, setCandidates] = useState<ICarrierCandidate[]>([])
    const [showCandidates, setShowCandidates] = useState(false)
    const [backdrop, setBackdrop] = useState(true)
    const [busy, setBusy] = useState(false)
    const [alert, setAlert] = useState<{ severity: 'error' | 'success' | 'warning' | 'info', text: string } | null>(null)

    const [newCarrier, setNewCarrier] = useState('')
    const [newTier, setNewTier] = useState('wholesale')
    const [newNote, setNewNote] = useState('')

    const describe = (error: any): string => {
        const d = error?.response?.data
        if (d?.messages) {
            return Object.keys(d.messages).map((k) => d.messages[k]).join(' ')
        }
        return d?.error || error?.message || 'Request failed'
    }

    const load = () => {
        Promise.all([
            CarrierBlocksActions.getRules({}, 1, 200),
            CarrierBlocksActions.getStats(7),
        ])
            .then(([rules, s]: any[]) => {
                setData(rules)
                setStats(s)
                setBackdrop(false)
            })
            .catch((error) => {
                setBackdrop(false)
                setAlert({ severity: 'error', text: describe(error) })
            })
    }

    useEffect(load, [])

    const loadCandidates = () => {
        setShowCandidates(true)
        CarrierBlocksActions.getCandidates(7, 40)
            .then((res: any) => setCandidates(res.candidates || []))
            .catch((error) => setAlert({ severity: 'error', text: describe(error) }))
    }

    const create = (carrier: string, tier: string, note: string) => {
        if (carrier.trim() === '') {
            setAlert({ severity: 'error', text: 'Carrier name is required.' })
            return
        }
        setBusy(true)
        CarrierBlocksActions.createRule({ carrier_name: carrier, tier, note })
            .then(() => {
                setBusy(false)
                setNewCarrier('')
                setNewNote('')
                setAlert({ severity: 'success', text: 'Rule added.' })
                load()
                if (showCandidates) { loadCandidates() }
            })
            .catch((error) => {
                setBusy(false)
                setAlert({ severity: 'error', text: describe(error) })
            })
    }

    const toggle = (rule: ICarrierRule) => {
        setBusy(true)
        CarrierBlocksActions.updateRule(rule.id, { active: !rule.active })
            .then(() => { setBusy(false); load() })
            .catch((error) => { setBusy(false); setAlert({ severity: 'error', text: describe(error) }) })
    }

    const remove = (rule: ICarrierRule) => {
        // Deactivating is the reversible option and the one to reach for during an incident;
        // deleting is for a carrier that should never have been listed.
        if (!window.confirm(`Delete the rule for "${rule.carrier_name}" (${rule.tier})? `
            + `Deactivating it instead is reversible.`)) {
            return
        }
        setBusy(true)
        CarrierBlocksActions.deleteRule(rule.id)
            .then(() => { setBusy(false); setAlert({ severity: 'success', text: 'Rule deleted.' }); load() })
            .catch((error) => { setBusy(false); setAlert({ severity: 'error', text: describe(error) }) })
    }

    const rules = data?.rules?.data || []
    const cost = stats?.cost

    return (
        <>
            <Container className={mainStyles.mainContainer} maxWidth={false}>
                {data !== null ?
                    <Grid container spacing={1}>

                        {/* ------------------------------------------------ state */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3}>
                                        <span className={`${styles.Status} ${
                                            !data.active ? styles.StatusExpired
                                                : data.dry_run ? styles.StatusCandidate : styles.StatusActive}`}>
                                            {!data.active ? 'OFF' : data.dry_run ? 'DRY RUN' : 'LIVE'}
                                        </span>
                                        &nbsp;<b>mode: {data.mode}</b>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <div className={styles.Evidence}>
                                            {rules.filter((r) => r.enforced_now).length} of {rules.length} rules
                                            are enforced right now. A rule on the <b>attack</b> tier does
                                            nothing while the mode is <b>light</b> — that is configuration,
                                            not a fault.
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <BlueBtn fullWidth={true} href="/carrier-settings">
                                            Settings &amp; modes
                                        </BlueBtn>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>

                        {/* ------------------------------------------------ effect + cost */}
                        {stats !== null ?
                            <Grid item xs={12}>
                                <div className={styles.Panel}>
                                    <h4>Last 7 days</h4>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={3}>
                                            <div className={styles.Evidence}>
                                                <b>Wholesale</b><br />
                                                rejected {stats.totals.wholesale.rejected}<br />
                                                would block {stats.totals.wholesale.would_block}
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <div className={styles.Evidence}>
                                                <b>Attack</b><br />
                                                rejected {stats.totals.attack.rejected}<br />
                                                would block {stats.totals.attack.would_block}
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            {cost && cost.available ?
                                                <div className={styles.Evidence}>
                                                    <b>Cost — qualified bookings: {cost.qualified_bookings}</b>
                                                    &nbsp;(direct leads {cost.direct_leads}) over{' '}
                                                    {cost.flagged_calls} flagged calls
                                                    <div className={styles.EvidenceMuted}>
                                                        raw tags {cost.booking_tags_raw} — not the number to
                                                        act on. Qualified means {cost.qualifies_when}.
                                                        Expect ~0.33/day; escalate above 2/day.
                                                    </div>
                                                    <div className={styles.Hint}>
                                                        Only dry-run verdicts can carry this: a rejected
                                                        call never reaches an agent to be classified.
                                                    </div>
                                                </div>
                                                :
                                                <div className={styles.EvidenceWarn}>
                                                    Cost cannot be measured: {cost?.reason || 'no data yet'}
                                                </div>
                                            }
                                        </Grid>
                                    </Grid>
                                </div>
                            </Grid>
                            : null}

                        {/* ------------------------------------------------ add */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <h4>Add a carrier</h4>
                                <div className={styles.Hint}>
                                    The name is matched <b>literally</b> against the carrier stored on
                                    the caller's number, so paste it exactly — a tidied-up name is a
                                    rule that never fires and never says so. <b>Onvoy</b> and
                                    <b> Onvoy, LLC</b> are two different carriers and need two rules.
                                </div>
                                <Grid container spacing={2} alignItems="flex-end">
                                    <Grid item xs={12} md={4}>
                                        <label className={formStyles.FormLabel}>Carrier name</label>
                                        <input
                                            className={formStyles.FormControl}
                                            value={newCarrier}
                                            onChange={(e) => setNewCarrier(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <label className={formStyles.FormLabel}>Tier</label>
                                        <select
                                            className={formStyles.FormControl}
                                            value={newTier}
                                            onChange={(e) => setNewTier(e.target.value)}
                                        >
                                            <option value="wholesale">wholesale</option>
                                            <option value="attack">attack</option>
                                        </select>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <label className={formStyles.FormLabel}>Note</label>
                                        <input
                                            placeholder="Why — measured numbers help later"
                                            className={formStyles.FormControl}
                                            value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <BlueBtn fullWidth={true} disabled={busy}
                                            onClick={() => create(newCarrier, newTier, newNote)}>
                                            Add
                                        </BlueBtn>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>

                        {/* ------------------------------------------------ rules */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <h4>Carrier rules</h4>
                                {rules.length === 0 ?
                                    <div className={styles.Empty}>No rules yet.</div>
                                    :
                                    rules.map((rule) => (
                                        <div className={styles.HistoryRow} key={rule.id}>
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid item xs={12} md={4}>
                                                    <b>{rule.carrier_name}</b>
                                                    <div className={styles.EvidenceMuted}>{rule.note}</div>
                                                </Grid>
                                                <Grid item xs={6} md={2}>
                                                    <span className={styles.Evidence}>{rule.tier}</span>
                                                </Grid>
                                                <Grid item xs={6} md={3}>
                                                    <Tooltip title={rule.active
                                                        ? 'Active. Enforced only when the mode covers its tier.'
                                                        : 'Inactive — never enforced.'}>
                                                        <span className={rule.enforced_now
                                                            ? styles.StatusActive : styles.EvidenceMuted}>
                                                            {rule.active
                                                                ? (rule.enforced_now ? 'enforced now' : 'active, not in this mode')
                                                                : 'inactive'}
                                                        </span>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid item xs={6} md={2}>
                                                    <GreyInverseBtn fullWidth={true} disabled={busy}
                                                        onClick={() => toggle(rule)}>
                                                        {rule.active ? 'Deactivate' : 'Activate'}
                                                    </GreyInverseBtn>
                                                </Grid>
                                                <Grid item xs={6} md={1}>
                                                    <GreyInverseBtn fullWidth={true} disabled={busy}
                                                        onClick={() => remove(rule)}>
                                                        Delete
                                                    </GreyInverseBtn>
                                                </Grid>
                                            </Grid>
                                        </div>
                                    ))
                                }
                            </div>
                        </Grid>

                        {/* ------------------------------------------------ candidates */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <h4>Candidates — carriers not on any list</h4>
                                <div className={styles.Hint}>
                                    Ranked by fewest leads, then highest junk share, over 7 days and at
                                    least 40 calls. Carriers move, so re-check this weekly rather than
                                    trusting the seeded list forever.
                                </div>
                                {!showCandidates ?
                                    <BlueBtn onClick={loadCandidates}>Load candidates</BlueBtn>
                                    : candidates.length === 0 ?
                                        <div className={styles.Empty}>Nothing over the threshold.</div>
                                        :
                                        candidates.map((c) => (
                                            <div className={styles.HistoryRow} key={c.carrier_name}>
                                                <Grid container spacing={1} alignItems="center">
                                                    <Grid item xs={12} md={5}><b>{c.carrier_name}</b></Grid>
                                                    <Grid item xs={6} md={2}>
                                                        <span className={styles.Evidence}>
                                                            {c.reached} calls
                                                        </span>
                                                    </Grid>
                                                    <Grid item xs={6} md={2}>
                                                        <span className={styles.Evidence}>
                                                            junk {c.junk_pct}%
                                                        </span>
                                                    </Grid>
                                                    <Grid item xs={6} md={1}>
                                                        {/* Leads are the veto here: a carrier producing
                                                            leads does not belong on either list however
                                                            dirty it looks. */}
                                                        <span className={c.leads > 0
                                                            ? styles.EvidenceWarn : styles.Evidence}>
                                                            {c.leads} leads
                                                        </span>
                                                    </Grid>
                                                    <Grid item xs={6} md={2}>
                                                        <GreyInverseBtn fullWidth={true} disabled={busy}
                                                            onClick={() => create(c.carrier_name, 'wholesale',
                                                                `${c.leads} leads / ${c.reached} reached / ${c.junk_pct}% junk`)}>
                                                            Add to wholesale
                                                        </GreyInverseBtn>
                                                    </Grid>
                                                </Grid>
                                            </div>
                                        ))
                                }
                            </div>
                        </Grid>
                    </Grid>
                    : <></>}
            </Container>

            <Snackbar
                open={alert !== null}
                autoHideDuration={6000}
                onClose={() => setAlert(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={() => setAlert(null)} severity={alert?.severity || 'error'} variant="filled">
                    <div className={backdropStyles.AlertError}>{alert?.text}</div>
                </Alert>
            </Snackbar>

            <Backdrop className={backdropStyles.Backdrop} open={backdrop}>
                <CircularProgress className={backdropStyles.BackdropProgress} />&nbsp;&nbsp;
                <p className={backdropStyles.BackdropProgress}>Loading...</p>
            </Backdrop>
        </>
    )
}

export default CarrierBlocks
